import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
} from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { CommentDraft, ThreadViewModel } from '../../models';
import { CommentInputComponent } from '../comment-input/comment-input.component';
import { CommentInputValue } from '@anecdotes/commenting';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { WindowHelperService } from '@anecdotes/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-savable-comment-input',
  templateUrl: './savable-comment-input.component.html',
  styleUrls: ['./../comment-input/comment-input.component.scss', './savable-comment-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SavableCommentInputComponent),
      multi: true,
    },
  ],
})
export class SavableCommentInputComponent extends CommentInputComponent implements OnInit, AfterViewInit {
  private localStorage: Storage;

  @HostBinding('class')
  protected get classes(): string {
    return this.isDraftLong ? 'pb-6' : '';
  }

  @Input()
  draftMessage: string;

  @Input()
  resourceToCreateThreadFor: ThreadViewModel;

  @Input()
  localStorageKey: string;

  get shouldDraftBeVisible(): boolean {
    return !!this.textControl?.value?.body && !this.isInFocus;
  }

  get isDraftLong(): boolean {
    return this.shouldDraftBeVisible && this.textControl?.value?.body?.length > 40;
  }

  constructor(protected cd: ChangeDetectorRef, protected windowHelper: WindowHelperService, domSanitizer: DomSanitizer) {
    super(cd, windowHelper, domSanitizer);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.localStorage = this.windowHelper.getWindow().localStorage;
  }

  ngAfterViewInit(): void {
    if (this.draftMessage) {
      this.writeValue({ body: this.draftMessage } as CommentInputValue);
    }
    this.saveDraftCommentOnEditing();
    this.cd.detectChanges();
  }

  buildTranslationKey(relativeKey: string): string {
    return `commenting.${relativeKey}`;
  }

  reset(): void {
    super.reset();
    this.removeDraftComment();
  }

  private saveDraftCommentOnEditing(): void {
    this.textControl.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((comment: CommentInputValue) => {
        if (this.localStorageKey && comment?.body) {
          const commentDraft: CommentDraft = {
            body: comment.body,
            resourceToCreateThreadFor: this.resourceToCreateThreadFor,
          };
          this.localStorage.setItem(this.localStorageKey, JSON.stringify(commentDraft));
        } else {
          this.removeDraftComment();
        }
      });
  }

  private removeDraftComment(): void {
    if (this.localStorageKey) {
      this.localStorage.removeItem(this.localStorageKey);
    }
  }
}
